import React, { useState, useRef } from 'react';
import {
  Button,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Text,
  Box,
  HStack,
  Badge,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useToast,
  Spinner,
  VStack,
  useColorModeValue,
  Tooltip,
  Flex,
  useBreakpointValue,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton
} from '@chakra-ui/react';
import {
  FloppyDisk,
  CaretDown,
  DotsThreeOutline,
  Trash,
  Pencil,
  ClockCounterClockwise,
  Calendar,
  ArrowSquareOut,
  Warning,
  CheckCircle,
  NotePencil
} from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import { useCreateSite } from '../../context/CreateSiteContext';
import { format, formatDistance } from 'date-fns';

const MotionMenuItem = motion(MenuItem);

/**
 * DraftManager Component
 * @param {Object} props
 * @param {boolean} [props.isMobile=false] Whether the component is being rendered in mobile view
 */
export const DraftManager = ({ isMobile = false }) => {
  const {
    drafts,
    loadDrafts,
    loadDraftById,
    deleteDraft,
    handleSaveAsDraft,
    isDirty,
    currentDraftId,
    savingDraft,
    siteData
  } = useCreateSite();

  const [isOpen, setIsOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedDraft, setSelectedDraft] = useState(null);
  const toast = useToast();
  const menuButtonRef = useRef();

  // Colors
  const menuBg = useColorModeValue('white', 'bg.surface');
  const menuItemBgHover = useColorModeValue('gray.100', 'bg.accent');
  const menuItemBgActive = useColorModeValue('blue.50', 'bg.default');
  const borderColor = useColorModeValue('gray.200', 'border.default');
  const textMuted = useColorModeValue('text.muted', 'text.muted');
  const accentColor = useColorModeValue('brand.primary', 'brand.primary');

  // Format date for display
  const formatDate = (date) => {
    if (!date) return 'Unknown date';
    return format(new Date(date), 'MMM d, yyyy • h:mm a');
  };

  // Get relative time
  const getRelativeTime = (date) => {
    if (!date) return '';
    return formatDistance(new Date(date), new Date(), { addSuffix: true });
  };

  const handleOpenDeleteModal = (draft) => {
    setSelectedDraft(draft);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedDraft) return;

    try {
      await deleteDraft(selectedDraft.id);
      setIsDeleteModalOpen(false);
      setSelectedDraft(null);
      
      // Close the drawer if it's open
      if (isDrawerOpen) {
        setIsDrawerOpen(false);
      }
    } catch (error) {
      console.error('Error deleting draft:', error);
      toast({
        title: 'Error deleting draft',
        description: error.message,
        status: 'error',
        duration: 3000
      });
    }
  };

  const handleLoadDraft = (draft) => {
    loadDraftById(draft.id);
    setIsOpen(false);
    
    // Close the drawer if it's open
    if (isDrawerOpen) {
      setIsDrawerOpen(false);
    }
  };
  
  // Draft list content that can be reused in both menu and drawer
  const DraftListContent = () => (
    <>
      {drafts.length === 0 ? (
        <Box p={4} textAlign="center">
          <Text color={textMuted} fontSize="sm">
            No saved drafts yet. Save your current progress to create one.
          </Text>
          <Button
            mt={3}
            size="sm"
            leftIcon={<FloppyDisk />}
            onClick={() => {
              handleSaveAsDraft();
              if (isDrawerOpen) setIsDrawerOpen(false);
            }}
            colorScheme="yellow"
            isLoading={savingDraft}
            loadingText="Saving..."
            isDisabled={!isDirty && !currentDraftId}
          >
            Save Current Progress
          </Button>
        </Box>
      ) : (
        <>
          <Box py={1} maxH="300px" overflowY="auto">
            {drafts.map((draft) => {
              const isCurrentDraft = draft.id === currentDraftId;
              
              return (
                <Box
                  key={draft.id}
                  bg={isCurrentDraft ? menuItemBgActive : 'transparent'}
                  borderRadius="md"
                  p={2}
                  mb={1}
                  borderWidth={isCurrentDraft ? "1px" : "0"}
                  borderColor={isCurrentDraft ? accentColor : "transparent"}
                  _hover={{ 
                    backgroundColor: isCurrentDraft ? menuItemBgActive : menuItemBgHover
                  }}
                  transition="background 0.2s"
                  onClick={(e) => {
                    // Prevent click propagation to avoid toggling menu
                    e.stopPropagation();
                  }}
                >
                  <HStack justifyContent="space-between" width="100%">
                    <VStack align="start" spacing={0} flex={1}>
                      <HStack align="center">
                        <Text fontWeight={isCurrentDraft ? "semibold" : "medium"} fontSize="sm" noOfLines={1}>
                          {draft.siteData.name || "Untitled Site"}
                        </Text>
                        {isCurrentDraft && (
                          <Badge size="sm" colorScheme="green" variant="solid" fontSize="2xs">
                            Current
                          </Badge>
                        )}
                      </HStack>

                      <HStack fontSize="xs" color={textMuted} spacing={3} mt={1}>
                        <Flex align="center">
                          <ClockCounterClockwise size={12} style={{ marginRight: '4px' }} />
                          <Text>{getRelativeTime(draft.updatedAt)}</Text>
                        </Flex>
                        <Flex align="center">
                          <Calendar size={12} style={{ marginRight: '4px' }} />
                          <Text>{format(new Date(draft.updatedAt), 'MMM d')}</Text>
                        </Flex>
                      </HStack>
                    </VStack>

                    <HStack>
                      {!isCurrentDraft && (
                        <Tooltip label="Load draft">
                          <IconButton
                            icon={<ArrowSquareOut />}
                            aria-label="Load draft"
                            size="sm"
                            variant="ghost"
                            onClick={() => handleLoadDraft(draft)}
                          />
                        </Tooltip>
                      )}
                      <Tooltip label="Delete draft">
                        <IconButton
                          icon={<Trash />}
                          aria-label="Delete draft"
                          size="sm"
                          variant="ghost"
                          colorScheme="red"
                          onClick={() => handleOpenDeleteModal(draft)}
                        />
                      </Tooltip>
                    </HStack>
                  </HStack>
                </Box>
              );
            })}
          </Box>

          {!isMobile && <MenuDivider />}

          <Box p={2}>
            <Button
              leftIcon={<FloppyDisk />}
              size="sm"
              colorScheme="yellow"
              width="full"
              onClick={() => {
                handleSaveAsDraft();
                if (isDrawerOpen) setIsDrawerOpen(false);
              }}
              isLoading={savingDraft}
              loadingText="Saving..."
              isDisabled={!isDirty && currentDraftId}
            >
              {currentDraftId ? 'Update Current Draft' : 'Save New Draft'}
            </Button>
          </Box>
        </>
      )}
    </>
  );

  // Render different UI for mobile and desktop
  if (isMobile) {
    return (
      <>
        <Button
          leftIcon={<NotePencil />}
          variant="outline"
          colorScheme="yellow"
          size="sm"
          flex={1}
          onClick={() => setIsDrawerOpen(true)}
        >
          Drafts
          {drafts.length > 0 && (
            <Badge 
              ml={2} 
              colorScheme="yellow"
              borderRadius="full"
            >
              {drafts.length}
            </Badge>
          )}
        </Button>

        {/* Mobile Drawer */}
        <Drawer
          isOpen={isDrawerOpen}
          placement="bottom"
          onClose={() => setIsDrawerOpen(false)}
          size="md"
        >
          <DrawerOverlay />
          <DrawerContent borderTopRadius="lg">
            <DrawerCloseButton />
            <DrawerHeader borderBottomWidth="1px">
              <Text>Saved Drafts</Text>
              <Text fontSize="sm" color={textMuted}>
                {drafts.length} {drafts.length === 1 ? 'draft' : 'drafts'} available
              </Text>
            </DrawerHeader>
            <DrawerBody p={2} maxH="80vh">
              <DraftListContent />
            </DrawerBody>
          </DrawerContent>
        </Drawer>

        {/* Delete confirmation modal */}
        <Modal isOpen={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)}>
          <ModalOverlay />
          <ModalContent mx={4}>
            <ModalHeader>Delete Draft</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack align="start" spacing={4}>
                <HStack color="red.500">
                  <Warning size={24} />
                  <Text fontWeight="medium">This action cannot be undone</Text>
                </HStack>
                
                <Text>
                  Are you sure you want to delete the draft for <strong>{selectedDraft?.siteData.name || "Untitled Site"}</strong>?
                </Text>
                
                {selectedDraft?.id === currentDraftId && (
                  <Box p={3} bg="yellow.50" borderRadius="md">
                    <Text fontSize="sm" color="yellow.800">
                      This is your currently loaded draft. Deleting it will not affect your current work, but you won't be able to reload it later.
                    </Text>
                  </Box>
                )}
              </VStack>
            </ModalBody>

            <ModalFooter>
              <Button mr={3} onClick={() => setIsDeleteModalOpen(false)}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={handleConfirmDelete}>
                Delete Draft
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </>
    );
  }

  // Desktop version with dropdown menu
  return (
    <>
      <Menu isOpen={isOpen} onClose={() => setIsOpen(false)} onOpen={() => setIsOpen(true)}>
        <Tooltip 
          label={drafts.length ? "Manage drafts" : "Save as draft"} 
          placement="top"
          openDelay={500}
        >
          <MenuButton
            as={Button}
            ref={menuButtonRef}
            leftIcon={<FloppyDisk />}
            rightIcon={drafts.length > 0 ? <CaretDown /> : null}
            variant="ghost"
            // Removed onClick that auto-saves when no drafts exist.
            // The MenuButton's default behavior is to toggle the menu.
            isLoading={savingDraft}
            loadingText="Saving..."
          >
            Drafts
            {drafts.length > 0 && (
              <Badge 
                ml={2} 
                colorScheme="yellow"
                borderRadius="full"
              >
                {drafts.length}
              </Badge>
            )}
          </MenuButton>
        </Tooltip>

        <MenuList
          zIndex={100}
          bgColor={menuBg}
          borderColor={borderColor}
          boxShadow="lg"
          p={2}
          minW="340px"
        >
          <Box px={3} pb={2}>
            <Text fontWeight="medium" fontSize="md" fontFamily="heading">
              Draft Manager
            </Text>
            <Text fontSize="xs" color={textMuted}>
              Save your progress and continue later
            </Text>
          </Box>

          <MenuDivider />
          
          <DraftListContent />
        </MenuList>
      </Menu>

      {/* Delete confirmation modal */}
      <Modal isOpen={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete Draft</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="start" spacing={4}>
              <HStack color="red.500">
                <Warning size={24} />
                <Text fontWeight="medium">This action cannot be undone</Text>
              </HStack>
              
              <Text>
                Are you sure you want to delete the draft for <strong>{selectedDraft?.siteData.name || "Untitled Site"}</strong>?
              </Text>
              
              {selectedDraft?.id === currentDraftId && (
                <Box p={3} bg="yellow.50" borderRadius="md">
                  <Text fontSize="sm" color="yellow.800">
                    This is your currently loaded draft. Deleting it will not affect your current work, but you won't be able to reload it later.
                  </Text>
                </Box>
              )}
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button mr={3} onClick={() => setIsDeleteModalOpen(false)}>
              Cancel
            </Button>
            <Button colorScheme="red" onClick={handleConfirmDelete}>
              Delete Draft
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
